/**
 * 全屏模式事件通信服务
 * 用于在播放器组件和全局布局之间通信，实现全屏时临时取消390px宽度限制
 */

import { syncViewportCssVars, FULLSCREEN_HEIGHT_CSS } from '@/utils/viewportHeight.js';

class FullscreenEventService {
  constructor() {
    this.listeners = new Map();
    this.isFullscreen = false;
    this.deviceType = 'mobile';
    this.fullscreenDepth = 0;
    this.originalStyles = null;
  }

  /**
   * 检测设备类型 - 简化版本，主要看屏幕宽度
   */
  detectDeviceType() {
    const width = window.innerWidth;
    
    // 🔥 简化判断：主要看屏幕宽度
    if (width > 1024) {
      this.deviceType = 'desktop';
    } else if (width > 768) {
      this.deviceType = 'tablet';
    } else {
      this.deviceType = 'mobile';
    }
    
    console.log(`[FullscreenEventService] 设备检测结果: {width: ${width}, deviceType: '${this.deviceType}'}`);
    
    return this.deviceType;
  }

  /**
   * 进入全屏模式
   * @param {string} componentName - 组件名称 (lyric-player, music-list, video-modal)
   */
  enterFullscreen(componentName = 'unknown') {
    this.fullscreenDepth += 1;
    this.isFullscreen = true;
    this.detectDeviceType();
    syncViewportCssVars();
    
    console.log(`[FullscreenEventService] 进入全屏模式 - 组件: ${componentName}, 设备类型: ${this.deviceType}, depth: ${this.fullscreenDepth}`);
    
    if (this.fullscreenDepth === 1) {
      // 🔥 在所有设备上都应用全屏样式，确保突破CSS优先级限制
      this.applyFullscreenStyles(componentName);
    }
    
    // 发送全屏事件
    this.emit('fullscreen-enter', {
      componentName,
      deviceType: this.deviceType,
      timestamp: Date.now()
    });
  }

  /**
   * 退出全屏模式
   * @param {string} componentName - 组件名称
   */
  exitFullscreen(componentName = 'unknown') {
    // 未进入全屏时忽略多余的 exit，避免 fallback 恢复逻辑误删 Vue 绑定的设备类名
    if (this.fullscreenDepth <= 0) {
      console.log(`[FullscreenEventService] 忽略多余的退出全屏调用 - 组件: ${componentName}, depth: ${this.fullscreenDepth}`);
      return;
    }

    this.fullscreenDepth -= 1;

    console.log(`[FullscreenEventService] 退出全屏模式 - 组件: ${componentName}, 设备类型: ${this.deviceType}, depth: ${this.fullscreenDepth}`);

    if (this.fullscreenDepth === 0) {
      this.isFullscreen = false;
      syncViewportCssVars();
      // 🔥 直接恢复DOM样式
      this.restoreFullscreenStyles(componentName);
    }
    
    // 发送退出全屏事件
    this.emit('fullscreen-exit', {
      componentName,
      deviceType: this.deviceType,
      timestamp: Date.now()
    });
  }

  /**
   * 🔥 直接应用全屏样式到DOM元素
   * 通过直接操作style属性，确保突破CSS优先级限制
   */
  applyFullscreenStyles(componentName) {
    try {
      // 查找全局容器元素
      const contentContainer = document.querySelector('.content-container');
      const mainLayout = document.querySelector('.main-layout');
      const musicListContainer = document.querySelector('.music-list-container');
      const body = document.body;
      
      console.log(`[FullscreenEventService] 🔍 调试信息:`, {
        contentContainer: !!contentContainer,
        mainLayout: !!mainLayout,
        musicListContainer: !!musicListContainer,
        body: !!body,
        deviceType: this.deviceType,
        componentName
      });
      
      if (contentContainer) {
        // 🔥 修复：只有在尚未保存原始样式时才保存（避免在全屏状态下切换歌曲时覆盖原始样式）
        if (!this.originalStyles || !this.originalStyles.contentContainer) {
          // 保存原始样式（获取计算后的样式，而不是内联样式）
          const computedStyle = getComputedStyle(contentContainer);
          this.originalStyles = {
            contentContainer: {
              maxWidth: contentContainer.style.maxWidth || computedStyle.maxWidth,
              width: contentContainer.style.width || computedStyle.width,
              margin: contentContainer.style.margin || computedStyle.margin,
              padding: contentContainer.style.padding || computedStyle.padding,
              borderRadius: contentContainer.style.borderRadius || computedStyle.borderRadius,
              boxShadow: contentContainer.style.boxShadow || computedStyle.boxShadow,
              backgroundColor: contentContainer.style.backgroundColor || computedStyle.backgroundColor,
              position: contentContainer.style.position || computedStyle.position,
              top: contentContainer.style.top || computedStyle.top,
              left: contentContainer.style.left || computedStyle.left,
              transform: contentContainer.style.transform || computedStyle.transform,
              zIndex: contentContainer.style.zIndex || computedStyle.zIndex
            }
          };
          console.log(`[FullscreenEventService] 🔥 已保存原始样式`);
        } else {
          console.log(`[FullscreenEventService] 🔥 原始样式已存在，跳过保存（避免在全屏状态下切换歌曲时覆盖）`);
        }
        
        // 直接设置全屏样式
        contentContainer.style.setProperty('max-width', '100vw', 'important');
        contentContainer.style.setProperty('width', '100vw', 'important');
        contentContainer.style.setProperty('min-width', '100vw', 'important');
        contentContainer.style.setProperty('margin', '0', 'important');
        contentContainer.style.setProperty('padding', '0', 'important');
        contentContainer.style.setProperty('border-radius', '0', 'important');
        contentContainer.style.setProperty('box-shadow', 'none', 'important');
        contentContainer.style.setProperty('background-color', 'transparent', 'important');
        contentContainer.style.setProperty('position', 'fixed', 'important');
        contentContainer.style.setProperty('top', '0', 'important');
        contentContainer.style.setProperty('left', '0', 'important');
        contentContainer.style.setProperty('height', FULLSCREEN_HEIGHT_CSS, 'important');
        contentContainer.style.setProperty('max-height', FULLSCREEN_HEIGHT_CSS, 'important');
        contentContainer.style.setProperty('z-index', '10000', 'important');
        contentContainer.style.setProperty('transform', 'none', 'important');
        
        // 🔥 强制覆盖所有可能的容器限制
        contentContainer.style.setProperty('overflow', 'hidden', 'important');
        contentContainer.style.setProperty('display', 'block', 'important');
        
        // 🔥 使用CSS变量强制覆盖
        contentContainer.style.setProperty('--fullscreen-width', '100vw', 'important');
        contentContainer.style.setProperty('--fullscreen-height', FULLSCREEN_HEIGHT_CSS, 'important');
        
        // 🔥 直接注入CSS规则，确保最高优先级
        this.injectFullscreenCSS();
        
        // 🔥 同时处理父容器限制
        if (musicListContainer) {
          console.log(`[FullscreenEventService] 🔥 处理父容器 .music-list-container 的390px限制`);
          
          // 🔥 修复：只有在尚未保存父容器原始样式时才保存
          if (!this.originalStyles || !this.originalStyles.musicListContainer) {
            // 保存父容器原始样式（获取计算后的样式，而不是内联样式）
            const computedStyle = getComputedStyle(musicListContainer);
            if (!this.originalStyles) {
              this.originalStyles = {};
            }
            this.originalStyles.musicListContainer = {
              maxWidth: musicListContainer.style.maxWidth || computedStyle.maxWidth,
              width: musicListContainer.style.width || computedStyle.width,
              left: musicListContainer.style.left || computedStyle.left,
              transform: musicListContainer.style.transform || computedStyle.transform,
              margin: musicListContainer.style.margin || computedStyle.margin
            };
            console.log(`[FullscreenEventService] 🔥 已保存父容器原始样式`);
          } else {
            console.log(`[FullscreenEventService] 🔥 父容器原始样式已存在，跳过保存（避免在全屏状态下切换歌曲时覆盖）`);
          }
          
          // 强制突破390px限制
          musicListContainer.style.setProperty('max-width', '100vw', 'important');
          musicListContainer.style.setProperty('width', '100vw', 'important');
          musicListContainer.style.setProperty('left', '0', 'important');
          musicListContainer.style.setProperty('transform', 'none', 'important');
          musicListContainer.style.setProperty('margin', '0', 'important');
          musicListContainer.style.setProperty('position', 'fixed', 'important');
          musicListContainer.style.setProperty('top', '0', 'important');
          musicListContainer.style.setProperty('height', FULLSCREEN_HEIGHT_CSS, 'important');
          musicListContainer.style.setProperty('max-height', FULLSCREEN_HEIGHT_CSS, 'important');
          musicListContainer.style.setProperty('z-index', '9999', 'important');
          
          console.log(`[FullscreenEventService] 🔥 已突破父容器390px限制`);
        }
        
        console.log(`[FullscreenEventService] 已直接应用全屏样式到 .content-container`);
        
        // 🔍 验证样式是否真的被应用
        const computedStyle = getComputedStyle(contentContainer);
        console.log(`[FullscreenEventService] 🔍 样式验证:`, {
          maxWidth: computedStyle.maxWidth,
          width: computedStyle.width,
          height: computedStyle.height,
          position: computedStyle.position,
          top: computedStyle.top,
          left: computedStyle.left,
          zIndex: computedStyle.zIndex
        });
      } else {
        console.warn(`[FullscreenEventService] ⚠️ 未找到 .content-container 元素！`);
      }
      
      // 添加body类名作为备用方案
      body.classList.add('fullscreen-mode');
      body.classList.add(`fullscreen-${componentName}`);
      body.classList.add(`device-${this.deviceType}`);
      
      // 🔥 同时给父容器添加全屏类名
      if (musicListContainer) {
        musicListContainer.classList.add('fullscreen-mode');
        console.log(`[FullscreenEventService] 🔥 已给父容器添加全屏类名`);
      }
      
      console.log(`[FullscreenEventService] 🔍 Body类名:`, {
        classList: Array.from(body.classList),
        hasFullscreenMode: body.classList.contains('fullscreen-mode'),
        hasDeviceClass: body.classList.contains(`device-${this.deviceType}`)
      });
      
    } catch (error) {
      console.error('[FullscreenEventService] 应用全屏样式失败:', error);
    }
  }
  
  /**
   * 🔥 直接注入CSS规则，确保最高优先级
   */
  injectFullscreenCSS() {
    try {
      // 移除之前的样式表
      const existingStyle = document.getElementById('fullscreen-forced-styles');
      if (existingStyle) {
        existingStyle.remove();
      }
      
      // 创建新的样式表
      const style = document.createElement('style');
      style.id = 'fullscreen-forced-styles';
      style.textContent = `
        .content-container.fullscreen-mode {
          max-width: 100vw !important;
          width: 100vw !important;
          min-width: 100vw !important;
          height: ${FULLSCREEN_HEIGHT_CSS} !important;
          max-height: ${FULLSCREEN_HEIGHT_CSS} !important;
          position: fixed !important;
          top: 0 !important;
          left: 0 !important;
          z-index: 10000 !important;
          margin: 0 !important;
          padding: 0 !important;
          border-radius: 0 !important;
          box-shadow: none !important;
          background-color: transparent !important;
          transform: none !important;
          overflow: hidden !important;
          display: block !important;
        }
        
        /* 🔥 突破父容器390px限制 */
        .music-list-container.fullscreen-mode {
          max-width: 100vw !important;
          width: 100vw !important;
          left: 0 !important;
          transform: none !important;
          margin: 0 !important;
          position: fixed !important;
          top: 0 !important;
          height: ${FULLSCREEN_HEIGHT_CSS} !important;
          max-height: ${FULLSCREEN_HEIGHT_CSS} !important;
        }
        
        body.fullscreen-mode {
          overflow: hidden !important;
          margin: 0 !important;
          padding: 0 !important;
        }
      `;
      
      document.head.appendChild(style);
      console.log(`[FullscreenEventService] 🔥 已注入强制全屏CSS规则`);
      
    } catch (error) {
      console.error('[FullscreenEventService] 注入CSS失败:', error);
    }
  }
  
  /**
   * 🔥 恢复原始样式
   */
  restoreFullscreenStyles(componentName) {
    try {
      const contentContainer = document.querySelector('.content-container');
      const body = document.body;
      
      // 🔥 修复：即使 originalStyles 丢失，也要清除所有内联样式，让CSS规则生效
      if (contentContainer) {
        if (this.originalStyles && this.originalStyles.contentContainer) {
          // 如果有保存的原始样式，尝试恢复
          const original = this.originalStyles.contentContainer;
          
          // 清除所有内联样式
          contentContainer.style.removeProperty('max-width');
          contentContainer.style.removeProperty('width');
          contentContainer.style.removeProperty('margin');
          contentContainer.style.removeProperty('padding');
          contentContainer.style.removeProperty('border-radius');
          contentContainer.style.removeProperty('box-shadow');
          contentContainer.style.removeProperty('background-color');
          contentContainer.style.removeProperty('position');
          contentContainer.style.removeProperty('top');
          contentContainer.style.removeProperty('left');
          contentContainer.style.removeProperty('height');
          contentContainer.style.removeProperty('max-height');
          contentContainer.style.removeProperty('z-index');
          contentContainer.style.removeProperty('transform');
          
          console.log(`[FullscreenEventService] 已恢复原始样式到 .content-container`);
        } else {
          // 🔥 修复：即使 originalStyles 丢失，也要清除所有内联样式
          console.warn(`[FullscreenEventService] ⚠️ originalStyles 丢失，强制清除所有内联样式`);
          contentContainer.style.removeProperty('max-width');
          contentContainer.style.removeProperty('width');
          contentContainer.style.removeProperty('margin');
          contentContainer.style.removeProperty('padding');
          contentContainer.style.removeProperty('border-radius');
          contentContainer.style.removeProperty('box-shadow');
          contentContainer.style.removeProperty('background-color');
          contentContainer.style.removeProperty('position');
          contentContainer.style.removeProperty('top');
          contentContainer.style.removeProperty('left');
          contentContainer.style.removeProperty('height');
          contentContainer.style.removeProperty('max-height');
          contentContainer.style.removeProperty('z-index');
          contentContainer.style.removeProperty('transform');
          contentContainer.style.removeProperty('min-width');
          contentContainer.style.removeProperty('overflow');
          contentContainer.style.removeProperty('display');
          console.log(`[FullscreenEventService] 已强制清除 .content-container 的所有内联样式`);
        }
      }
      
      // 🔥 恢复父容器原始样式
      const musicListContainer = document.querySelector('.music-list-container');
      if (musicListContainer) {
        if (this.originalStyles && this.originalStyles.musicListContainer) {
          // 如果有保存的原始样式，恢复它
          console.log(`[FullscreenEventService] 🔥 恢复父容器 .music-list-container 的原始样式`);
          
          // 恢复父容器原始样式
          if (this.originalStyles.musicListContainer.maxWidth) {
            musicListContainer.style.setProperty('max-width', this.originalStyles.musicListContainer.maxWidth);
          } else {
            musicListContainer.style.removeProperty('max-width');
          }
          
          if (this.originalStyles.musicListContainer.width) {
            musicListContainer.style.setProperty('width', this.originalStyles.musicListContainer.width);
          } else {
            musicListContainer.style.removeProperty('width');
          }
          
          if (this.originalStyles.musicListContainer.left) {
            musicListContainer.style.setProperty('left', this.originalStyles.musicListContainer.left);
          } else {
            musicListContainer.style.removeProperty('left');
          }
          
          if (this.originalStyles.musicListContainer.transform) {
            musicListContainer.style.setProperty('transform', this.originalStyles.musicListContainer.transform);
          } else {
            musicListContainer.style.removeProperty('transform');
          }
          
          if (this.originalStyles.musicListContainer.margin) {
            musicListContainer.style.setProperty('margin', this.originalStyles.musicListContainer.margin);
          } else {
            musicListContainer.style.removeProperty('margin');
          }

          musicListContainer.style.removeProperty('position');
          musicListContainer.style.removeProperty('top');
          musicListContainer.style.removeProperty('height');
          musicListContainer.style.removeProperty('max-height');
          musicListContainer.style.removeProperty('z-index');
          
          console.log(`[FullscreenEventService] 🔥 已恢复父容器原始样式`);
        } else {
          // 🔥 修复：即使 originalStyles 丢失，也要强制恢复390px限制
          console.warn(`[FullscreenEventService] ⚠️ musicListContainer originalStyles 丢失，强制恢复390px限制`);
          
          // 清除所有内联样式，让CSS规则生效
          musicListContainer.style.removeProperty('max-width');
          musicListContainer.style.removeProperty('width');
          musicListContainer.style.removeProperty('left');
          musicListContainer.style.removeProperty('transform');
          musicListContainer.style.removeProperty('margin');
          musicListContainer.style.removeProperty('position');
          musicListContainer.style.removeProperty('top');
          musicListContainer.style.removeProperty('height');
          musicListContainer.style.removeProperty('max-height');
          musicListContainer.style.removeProperty('z-index');

          // 仅移除全屏相关类名；设备类型类由 Vue :class 管理，不可手动删除
          musicListContainer.classList.remove('fullscreen-mode');

          console.log(`[FullscreenEventService] 🔥 已强制清除父容器内联样式，390px限制将通过CSS规则恢复`);
        }
        
        // 🔥 移除父容器全屏类名
        musicListContainer.classList.remove('fullscreen-mode');
        console.log(`[FullscreenEventService] 🔥 已移除父容器全屏类名`);
      }
      
      // 移除body类名
      body.classList.remove('fullscreen-mode');
      body.classList.remove(`fullscreen-${componentName}`);
      body.classList.remove(`device-${this.deviceType}`);
      
      // 🔥 移除注入的CSS规则
      const injectedStyle = document.getElementById('fullscreen-forced-styles');
      if (injectedStyle) {
        injectedStyle.remove();
        console.log(`[FullscreenEventService] 🔥 已移除强制全屏CSS规则`);
      }
      
      // 🔥 清除保存的原始样式，确保下次进入全屏时重新保存
      this.originalStyles = null;
      console.log(`[FullscreenEventService] 🔥 已清除保存的原始样式，下次进入全屏时将重新保存`);
      
    } catch (error) {
      console.error('[FullscreenEventService] 恢复样式失败:', error);
    }
  }

  /**
   * 监听事件
   * @param {string} eventName - 事件名称
   * @param {Function} callback - 回调函数
   */
  on(eventName, callback) {
    if (!this.listeners.has(eventName)) {
      this.listeners.set(eventName, []);
    }
    this.listeners.get(eventName).push(callback);
  }

  /**
   * 移除事件监听
   * @param {string} eventName - 事件名称
   * @param {Function} callback - 回调函数
   */
  off(eventName, callback) {
    if (this.listeners.has(eventName)) {
      const callbacks = this.listeners.get(eventName);
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
      }
    }
  }

  /**
   * 发送事件
   * @param {string} eventName - 事件名称
   * @param {any} data - 事件数据
   */
  emit(eventName, data) {
    if (this.listeners.has(eventName)) {
      this.listeners.get(eventName).forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`[FullscreenEventService] 事件回调执行失败:`, error);
        }
      });
    }
  }

  /**
   * 获取当前全屏状态
   */
  getFullscreenStatus() {
    return {
      isFullscreen: this.isFullscreen,
      deviceType: this.deviceType
    };
  }

  /**
   * 清理所有监听器
   */
  destroy() {
    this.listeners.clear();
  }
}

// 创建单例实例
const fullscreenEventService = new FullscreenEventService();

export default fullscreenEventService;
